"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { useTenant } from "@/lib/tenant";
import { getSellerStepForStatus, getBuyerStepForStatus } from "@/lib/deals/types";
import type { DealWithRelations } from "@/lib/deals/types";
import type { AnonymizedDealRelations } from "@/lib/platform";
import { DealStepContent } from "@/components/deals/DealStepContent";
import { getStepHeader } from "@/components/deals/step-header";
type AnonymizedDeal = Omit<DealWithRelations, "seller" | "buyer"> & AnonymizedDealRelations;
interface DealStepModalProps {
  isOpen: boolean;
  onClose: () => void;
  deal: AnonymizedDeal;
  userRole: "seller" | "buyer";
  userId: string;
  onRefresh?: () => void;
}
export function DealStepModal(t0) {
  const $ = _c(21);
  const {
    isOpen,
    onClose,
    deal,
    userRole,
    userId,
    onRefresh
  } = t0;
  useTenant();
  const modalRef = useRef(null);
  const currentStep = userRole === "seller" ? getSellerStepForStatus(deal.status) : getBuyerStepForStatus(deal.status);
  const stepHeader = getStepHeader(currentStep, userRole);
  let t1;
  let t2;
  if ($[0] !== isOpen || $[1] !== onClose) {
    t1 = () => {
      const handleEscape = e => {
        if (e.key === "Escape") {
          onClose();
        }
      };
      if (isOpen) {
        document.addEventListener("keydown", handleEscape);
        document.body.style.overflow = "hidden";
      }
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
      };
    };
    t2 = [isOpen, onClose];
    $[0] = isOpen;
    $[1] = onClose;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onClose) {
    t3 = e_0 => {
      if (modalRef.current && !modalRef.current.contains(e_0.target as Node)) {
        onClose();
      }
    };
    $[4] = onClose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleBackdropClick = t3;
  if (!isOpen || !stepHeader) {
    return null;
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <X className="w-3 h-3" />;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== onClose) {
    t5 = <div className="relative flex justify-end items-center px-6 py-2 border-b border-zinc-200 flex-shrink-0 bg-gray-100"><button onClick={onClose} className="flex items-center justify-center px-2 gap-2 text-xs text-zinc-800 hover:text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors" aria-label={"Schlie\xDFen"}>{t4}Schließen</button></div>;
    $[7] = onClose;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== deal || $[10] !== onRefresh || $[11] !== userId || $[12] !== userRole) {
    t6 = <div className="flex-1 overflow-y-auto px-6 py-5"><DealStepContent dealId={deal.id} deal={deal} userRole={userRole} userId={userId} onRefresh={onRefresh} /></div>;
    $[9] = deal;
    $[10] = onRefresh;
    $[11] = userId;
    $[12] = userRole;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="px-6 py-4 flex-shrink-0 bg-gray-100" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t6) {
    t8 = <div ref={modalRef} className="relative w-full max-w-2xl mx-4 max-h-[90vh] bg-white rounded-2xl shadow-2xl animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 flex flex-col overflow-hidden">{t5}{t6}{t7}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== handleBackdropClick || $[19] !== t8) {
    t9 = <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200" onClick={handleBackdropClick} role="dialog" aria-modal="true" aria-labelledby="deal-step-modal-title">{t8}</div>;
    $[18] = handleBackdropClick;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}